/**
 * Internal Conditions Portal Component
 *
 * JWT-authenticated portal for internal staff to view and manage loan conditions.
 * Uses /internal/conditions/:loanId route with JWT authentication.
 *
 * This is the internal version of ConditionsPortal.tsx with the same features
 * but using JWT auth instead of token-based auth.
 *
 * Features:
 * - JWT-based authentication (requires active employment_status)
 * - Full loan summary with stats
 * - Condition filtering by timing and owner
 * - Bulk condition reassignment
 * - Route to Title & Escrow
 * - Document upload and submission
 * - Approval letter PDF download
 * - Background refresh with change detection
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Download,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle2,
  Circle,
  Phone,
  Mail,
  User,
  Users,
  Send,
  FileText,
  Loader2,
  Building,
  ChevronDown,
  Eye,
  ShieldX,
  Lock,
  Printer,
  FileCheck2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { ConditionsPortalService } from '@/services/conditionsPortalService';
import { ConditionsSection } from './ConditionsSection';
import { ConditionUploadModal } from './ConditionUploadModal';
import { TitleEscrowAssignModal } from './TitleEscrowAssignModal';
import { BulkReassignModal } from './BulkReassignModal';
import { CondoPudWarrantyModal } from './CondoPudWarrantyModal';
import { PortalPreviewSheet } from './PortalPreviewSheet';
import { BorrowerAssignModal } from './BorrowerAssignModal';
import { CleanApprovalSheet } from './CleanApprovalSheet';
import { MilestoneTimeline } from './MilestoneTimeline';
import type {
  ConditionsPortalData,
  ConditionsPortalToken,
  LoanTeamContact,
  TitleEscrowInfo
} from '@/types/conditions-portal';
import type { EncompassCondition } from '@/types/approval-letter';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('InternalConditionsPortal', 'conditions-portal');

// API configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Idle timeout for polling (5 minutes)
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

function isCleanApprovalEligible(data: ConditionsPortalData | null): boolean {
  if (!data) {
    return false;
  }

  const approvalDate = data.loanData.underwriting.approvedDate;
  if (approvalDate && !Number.isNaN(new Date(approvalDate).getTime())) {
    return true;
  }

  return (data.loanData.milestoneHistory || []).some((milestone) =>
    milestone.name?.trim().toLowerCase() === 'cond. approval' &&
    milestone.done === true &&
    Boolean(milestone.doneDate) &&
    !Number.isNaN(new Date(milestone.doneDate as string).getTime())
  );
}

function hasCleanApprovalQuery(): boolean {
  return typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('cleanApproval') === '1';
}

function removeCleanApprovalQuery(): void {
  if (typeof window === 'undefined' || !hasCleanApprovalQuery()) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  params.delete('cleanApproval');
  const search = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
  );
}

interface PortalState {
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  data: ConditionsPortalData | null;
  lastRefresh: Date | null;
}

export const InternalConditionsPortal = () => {
  const { loanId } = useParams<{ loanId: string }>();
  const { user } = useAuth();
  const { toast } = useToast();

  const [state, setState] = useState<PortalState>({
    loading: true,
    error: null,
    errorCode: null,
    data: null,
    lastRefresh: null
  });

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<EncompassCondition | null>(null);
  const [showOnlyOpen, setShowOnlyOpen] = useState(true);
  const [timingFilter, setTimingFilter] = useState<'all' | 'ptd' | 'ptf' | 'ac'>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [submittingToProcessing, setSubmittingToProcessing] = useState(false);
  const [hasNewData, setHasNewData] = useState(false);
  const [pendingData, setPendingData] = useState<ConditionsPortalData | null>(null);
  const [titleEscrowModalOpen, setTitleEscrowModalOpen] = useState(false);
  const [bulkReassignModalOpen, setBulkReassignModalOpen] = useState(false);
  const [warrantyModalOpen, setWarrantyModalOpen] = useState(false);
  const [warrantyCondition, setWarrantyCondition] = useState<EncompassCondition | null>(null);
  const [previewSheetOpen, setPreviewSheetOpen] = useState(false);
  const [previewPortalType, setPreviewPortalType] = useState<'title_escrow' | 'borrower'>('title_escrow');
  const [titleEscrowAssignedIds, setTitleEscrowAssignedIds] = useState<Set<string>>(new Set());
  const [titleEscrowNotes, setTitleEscrowNotes] = useState<Record<string, string>>({});
  const [borrowerAssignModalOpen, setBorrowerAssignModalOpen] = useState(false);
  const [borrowerAssignedIds, setBorrowerAssignedIds] = useState<Set<string>>(new Set());
  const [borrowerNotes, setBorrowerNotes] = useState<Record<string, string>>({});
  const [cleanApprovalSheetOpen, setCleanApprovalSheetOpen] = useState(false);
  const [activeTokens, setActiveTokens] = useState<{
    loProcessor: ConditionsPortalToken | null;
    titleEscrow: ConditionsPortalToken | null;
  }>({ loProcessor: null, titleEscrow: null });
  const [closePortalDialogOpen, setClosePortalDialogOpen] = useState(false);
  const [closingPortals, setClosingPortals] = useState(false);
  const [portalCloseSelections, setPortalCloseSelections] = useState<{ borrower: boolean; titleEscrow: boolean }>({ borrower: false, titleEscrow: false });
  const [jwt, setJwt] = useState<string | null>(null);

  // Track user activity for idle detection
  const lastActivityRef = useRef(Date.now());
  const cleanApprovalAutoOpenHandledRef = useRef(false);

  // Update last activity on user interaction
  useEffect(() => {
    const trackActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, trackActivity, { passive: true }));
    return () => {
      events.forEach(e => document.removeEventListener(e, trackActivity));
    };
  }, []);

  /**
   * Get fresh JWT for API calls
   */
  const getJwt = useCallback(async (): Promise<string | null> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token || null;
    setJwt(token);
    return token;
  }, []);

  /**
   * Fetch portal data via internal endpoint
   */
  const fetchPortalData = useCallback(async (showLoadingState = true) => {
    if (!loanId || !user?.id) {
return;
}

    // Only show loading skeleton on initial load, not on refetches when we have data
    if (showLoadingState) {
      setState(prev => {
        if (!prev.data) {
          return { ...prev, loading: true, error: null };
        }
        return prev;
      });
    }

    try {
      const token = await getJwt();

      if (!token) {
        setState({
          loading: false,
          error: 'Session expired. Please log in again.',
          errorCode: 'UNAUTHORIZED',
          data: null,
          lastRefresh: null
        });
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'validate-internal', loanId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        log.error('Internal portal validation failed', { loanId, error: result.error });
        setState({
          loading: false,
          error: result.error || 'Failed to load portal data',
          errorCode: result.code || 'SERVER_ERROR',
          data: null,
          lastRefresh: null
        });
        return;
      }

      log.info('Internal portal data loaded', { loanId });
      setState({
        loading: false,
        error: null,
        errorCode: null,
        data: result.data,
        lastRefresh: new Date()
      });

    } catch (error) {
      log.error('Internal portal fetch error', { loanId }, error);
      setState({
        loading: false,
        error: 'Failed to load portal data',
        errorCode: 'SERVER_ERROR',
        data: null,
        lastRefresh: null
      });
    }
     
  }, [loanId, user?.id, getJwt]);

  // Initial load - depend on user.id only to prevent refetch on token refreshes
  useEffect(() => {
    if (loanId && user?.id) {
      fetchPortalData();
    }
     
  }, [loanId, user?.id, fetchPortalData]);

  useEffect(() => {
    cleanApprovalAutoOpenHandledRef.current = false;
  }, [loanId]);

  useEffect(() => {
    if (cleanApprovalAutoOpenHandledRef.current || !hasCleanApprovalQuery() || !state.data) {
      return;
    }

    cleanApprovalAutoOpenHandledRef.current = true;
    if (isCleanApprovalEligible(state.data)) {
      setCleanApprovalSheetOpen(true);
    } else {
      toast({
        title: 'Clean Approval is not available yet',
        description: 'This workflow becomes available after conditional approval.'
      });
      removeCleanApprovalQuery();
    }
  }, [state.data, toast]);

  // Fetch title/escrow and borrower assignments for portal preview filtering
  const fetchAllAssignments = useCallback(async () => {
    if (!loanId || !state.data) {
return;
}

    // Fetch title/escrow assignments
    const { data: teAssignments } = await supabase
      .from('condition_title_escrow_assignments')
      .select('condition_external_id, note')
      .eq('encompass_loan_id', loanId);

    if (teAssignments && teAssignments.length > 0) {
      setTitleEscrowAssignedIds(new Set(teAssignments.map(a => a.condition_external_id)));
      const notes: Record<string, string> = {};
      for (const a of teAssignments) {
        if (a.note) {
notes[a.condition_external_id] = a.note;
}
      }
      setTitleEscrowNotes(notes);
    } else {
      setTitleEscrowAssignedIds(new Set());
      setTitleEscrowNotes({});
    }

    // Fetch borrower assignments
    const { data: bAssignments } = await supabase
      .from('condition_borrower_assignments')
      .select('condition_external_id, note')
      .eq('encompass_loan_id', loanId);

    if (bAssignments && bAssignments.length > 0) {
      setBorrowerAssignedIds(new Set(bAssignments.map(a => a.condition_external_id)));
      const bNotes: Record<string, string> = {};
      for (const a of bAssignments) {
        if (a.note) {
bNotes[a.condition_external_id] = a.note;
}
      }
      setBorrowerNotes(bNotes);
    } else {
      setBorrowerAssignedIds(new Set());
      setBorrowerNotes({});
    }

    // Fetch reassignment notes and merge on top of both audience maps
    // Reassignment notes override assignment notes for the same condition
    const { data: reassignNotes } = await supabase
      .from('condition_reassignment_notes')
      .select('condition_external_id, note')
      .eq('encompass_loan_id', loanId);

    if (reassignNotes && reassignNotes.length > 0) {
      // Merge into title/escrow notes
      setTitleEscrowNotes(prev => {
        const merged = { ...prev };
        for (const rn of reassignNotes) {
          if (rn.note) {
merged[rn.condition_external_id] = rn.note;
}
        }
        return merged;
      });
      // Merge into borrower notes
      setBorrowerNotes(prev => {
        const merged = { ...prev };
        for (const rn of reassignNotes) {
          if (rn.note) {
merged[rn.condition_external_id] = rn.note;
}
        }
        return merged;
      });
    }

    // Fetch active portal tokens
    const tokens = await ConditionsPortalService.getActiveTokensByType(loanId);
    setActiveTokens(tokens);
  }, [loanId, state.data]);

  useEffect(() => {
    fetchAllAssignments();
  }, [fetchAllAssignments]);

  // Background polling with visibility and idle checks
  useEffect(() => {
    if (!state.data) {
return;
}

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const checkForUpdates = async () => {
      // Skip if any assignment modal is open to prevent UI state resets
      if (bulkReassignModalOpen || titleEscrowModalOpen || borrowerAssignModalOpen || cleanApprovalSheetOpen) {
        log.debug('Skipping refresh - assignment modal is open');
        return;
      }

      // Skip if user is idle
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) {
        log.debug('Skipping refresh - user is idle');
        return;
      }

      try {
        const token = await getJwt();
        if (!token) {
return;
}

        const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ action: 'validate-internal', loanId })
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
return;
}

        // Compare conditions to see if anything changed
        const currentConditions = state.data?.loanData?.conditions;
        const newConditions = result.data?.loanData?.conditions;

        const currentAll = [
          ...(currentConditions?.ptd || []),
          ...(currentConditions?.ptf || []),
          ...(currentConditions?.ac || [])
        ];
        const newAll = [
          ...(newConditions?.ptd || []),
          ...(newConditions?.ptf || []),
          ...(newConditions?.ac || [])
        ];

        const currentCleared = currentAll.filter(c => !c.statusOpen).length;
        const newCleared = newAll.filter(c => !c.statusOpen).length;

        // Silently update lock status and deadline without triggering full refresh banner
        const currentLocked = state.data?.loanData?.isFileLocked;
        const newLocked = result.data?.loanData?.isFileLocked;
        const currentDeadline = state.data?.documentsNeededBy;
        const newDeadline = result.data?.documentsNeededBy;
        if (currentLocked !== newLocked || currentDeadline !== newDeadline) {
          setState(prev => prev.data ? {
            ...prev,
            data: {
              ...prev.data,
              documentsNeededBy: result.data?.documentsNeededBy ?? null,
              loanData: {
                ...prev.data.loanData,
                isFileLocked: result.data?.loanData?.isFileLocked ?? false,
                fileLockedByName: result.data?.loanData?.fileLockedByName ?? null,
                fileLockTime: result.data?.loanData?.fileLockTime ?? null
              }
            }
          } : prev);
        }

        if (currentAll.length !== newAll.length || currentCleared !== newCleared) {
          log.info('Background refresh detected changes', {
            oldCount: currentAll.length,
            newCount: newAll.length,
            oldCleared: currentCleared,
            newCleared
          });
          setPendingData(result.data);
          setHasNewData(true);
        }
      } catch (error) {
        log.warn('Background refresh failed silently', { error });
      }
    };

    const startPolling = () => {
      if (intervalId) {
return;
}
      intervalId = setInterval(checkForUpdates, 60000);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        log.debug('Tab hidden - stopping polling');
        stopPolling();
      } else {
        log.debug('Tab visible - resuming polling');
        startPolling();
        if (Date.now() - lastActivityRef.current <= IDLE_TIMEOUT_MS) {
          checkForUpdates();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (!document.hidden) {
      startPolling();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [state.data, loanId, getJwt, bulkReassignModalOpen, titleEscrowModalOpen, borrowerAssignModalOpen, cleanApprovalSheetOpen]);

  /**
   * Apply pending data when user clicks refresh banner
   */
  const applyPendingData = useCallback(() => {
    if (pendingData) {
      setState({
        loading: false,
        error: null,
        errorCode: null,
        data: pendingData,
        lastRefresh: new Date()
      });
      setPendingData(null);
      setHasNewData(false);
      toast({
        title: 'Updated',
        description: 'Conditions have been refreshed.'
      });
    }
  }, [pendingData, toast]);

  /**
   * Download approval letter PDF
   */
  const handleDownloadPdf = async () => {
    if (!loanId || downloadingPdf) {
return;
}

    try {
      setDownloadingPdf(true);
      const token = await getJwt();

      if (!token) {
        toast({ title: 'Session expired', description: 'Please refresh the page.', variant: 'destructive' });
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'get-pdf-internal', loanId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to generate PDF');
      }

      // Create blob and download
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename || 'approval-letter.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: 'Your approval letter PDF is downloading.'
      });

    } catch (error) {
      log.error('PDF download error', { error });
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Unable to download PDF',
        variant: 'destructive'
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  /**
   * Print conditions by owner role or title/escrow audience
   */
  const handlePrintConditions = (mode: 'title_escrow' | { owner: string }) => {
    if (!state.data) {
return;
}

    const { loanData, loanOfficer, company, conditionNotes: notes } = state.data;

    // Title/escrow audience print (existing behavior)
    if (mode === 'title_escrow') {
      const isAssigned = (c: EncompassCondition) =>
        titleEscrowAssignedIds.has(c.externalId) || titleEscrowAssignedIds.has(c.id) || titleEscrowAssignedIds.has(c.internalId);

      const all = [
        ...loanData.conditions.ptd.filter(c => !c.isRemoved),
        ...loanData.conditions.ptf.filter(c => !c.isRemoved),
        ...loanData.conditions.ac.filter(c => !c.isRemoved)
      ].filter(isAssigned).filter(c => !showOnlyOpen || c.statusOpen);

      return printConditionsHtml(loanData, loanOfficer, company, all, titleEscrowNotes, 'Title/Escrow View', showOnlyOpen);
    }

    // Owner-based print
    const ownerName = mode.owner;
    const all = [
      ...loanData.conditions.ptd.filter(c => !c.isRemoved),
      ...loanData.conditions.ptf.filter(c => !c.isRemoved),
      ...loanData.conditions.ac.filter(c => !c.isRemoved)
    ].filter(c => !showOnlyOpen || c.statusOpen);
    const filtered = ownerName === 'all'
      ? all
      : all.filter(c => (c.owner?.entityName || 'Unassigned') === ownerName);

    const label = ownerName === 'all' ? 'All Owners' : ownerName;
    printConditionsHtml(loanData, loanOfficer, company, filtered, notes || {}, label, showOnlyOpen);
  };

  /** Shared print renderer — opens a new window with inline HTML/CSS and calls print */
  const printConditionsHtml = (
    loanData: ConditionsPortalData['loanData'],
    loanOfficer: ConditionsPortalData['loanOfficer'],
    company: ConditionsPortalData['company'],
    conditions: EncompassCondition[],
    notes: Record<string, string>,
    label: string,
    openOnly: boolean = true
  ) => {
    const scopeLabel = openOnly ? 'Open Only' : 'All Conditions';
    const ROLE_ORDER = ['Processor', 'Loan Officer', 'Underwriter'];

    // Group by owner then sort by role order
    const grouped: Record<string, EncompassCondition[]> = {};
    for (const c of conditions) {
      const owner = c.owner?.entityName || 'Unassigned';
      if (!grouped[owner]) {
grouped[owner] = [];
}
      grouped[owner].push(c);
    }
    const sortedOwners = Object.keys(grouped).sort((a, b) => {
      const ai = ROLE_ORDER.findIndex(r => a.toLowerCase().includes(r.toLowerCase()));
      const bi = ROLE_ORDER.findIndex(r => b.toLowerCase().includes(r.toLowerCase()));
      const ao = ai === -1 ? (a === 'Unassigned' ? ROLE_ORDER.length + 1 : ROLE_ORDER.length) : ai;
      const bo = bi === -1 ? (b === 'Unassigned' ? ROLE_ORDER.length + 1 : ROLE_ORDER.length) : bi;
      return ao - bo;
    });

    const renderTimingTable = (title: string, conds: EncompassCondition[]) => {
      if (conds.length === 0) {
return '';
}
      return `
        <h3 style="font-size:12px;font-weight:600;margin:12px 0 4px;color:#374151;">${title}</h3>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#f3f4f6;">
              <th style="text-align:left;padding:5px 8px;border:1px solid #e5e7eb;">Status</th>
              <th style="text-align:left;padding:5px 8px;border:1px solid #e5e7eb;">Condition</th>
              <th style="text-align:left;padding:5px 8px;border:1px solid #e5e7eb;">Description</th>
            </tr>
          </thead>
          <tbody>
            ${conds.map((c, i) => {
              const note = notes[c.externalId || c.id || c.internalId || ''];
              const statusText = c.statusOpen ? 'Open' : 'Cleared';
              const statusColor = c.statusOpen ? '#92400e' : '#166534';
              return `
                <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'};">
                  <td style="padding:5px 8px;border:1px solid #e5e7eb;color:${statusColor};font-weight:500;white-space:nowrap;">${statusText}</td>
                  <td style="padding:5px 8px;border:1px solid #e5e7eb;font-weight:500;">${c.title || ''}</td>
                  <td style="padding:5px 8px;border:1px solid #e5e7eb;">
                    ${c.externalDescription || c.internalDescription || ''}
                    ${note ? `<div style="margin-top:4px;padding:4px 6px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:3px;color:#1e40af;font-size:11px;"><strong>Note:</strong> ${note}</div>` : ''}
                  </td>
                </tr>`;
            }).join('')}
          </tbody>
        </table>`;
    };

    const borrowerName = loanData.borrower?.name || '';
    const property = loanData.property
      ? `${loanData.property.address}, ${loanData.property.city}, ${loanData.property.state} ${loanData.property.zip}`
      : '';

    let sectionsHtml = '';
    for (const owner of sortedOwners) {
      const ownerConds = grouped[owner];
      const ptd = ownerConds.filter(c => c.priorTo === 'Docs');
      const ptf = ownerConds.filter(c => c.priorTo === 'Funding');
      const ac = ownerConds.filter(c => c.priorTo === 'Closing');
      const openCount = ownerConds.filter(c => c.statusOpen).length;

      sectionsHtml += `
        <div style="margin-top:16px;padding:12px;border-left:4px solid #183C2E;background:#f8faf9;border-radius:0 6px 6px 0;">
          <h2 style="font-size:14px;font-weight:600;color:#183C2E;margin:0;">${owner} <span style="font-weight:400;color:#6b7280;font-size:12px;">(${openCount > 0 ? `${openCount  } open` : 'all cleared'})</span></h2>
        </div>
        ${renderTimingTable('Prior to Docs', ptd)}
        ${renderTimingTable('Prior to Funding', ptf)}
        ${renderTimingTable('At Closing', ac)}`;
    }

    const html = `<!DOCTYPE html>
<html><head><title>Conditions – ${label} – ${loanData.loanNumber}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 24px; color: #1a1a1a; }
  @media print { body { margin: 12px; } }
</style></head><body>
  <div style="border-bottom:3px solid #183C2E;padding-bottom:12px;margin-bottom:16px;">
    <h1 style="font-size:18px;color:#183C2E;margin:0;">Conditions Report – ${label}</h1>
    <div style="display:flex;gap:24px;margin-top:8px;font-size:12px;color:#4b5563;">
      <span><strong>Loan #:</strong> ${loanData.loanNumber}</span>
      <span><strong>Borrower:</strong> ${borrowerName}</span>
    </div>
    <div style="font-size:12px;color:#4b5563;margin-top:2px;"><strong>Property:</strong> ${property}</div>
    ${loanOfficer ? `<div style="font-size:12px;color:#4b5563;margin-top:2px;"><strong>Loan Officer:</strong> ${loanOfficer.name || ''} ${loanOfficer.email ? `• ${  loanOfficer.email}` : ''}</div>` : ''}
  </div>
  <div style="font-size:12px;color:#6b7280;margin-bottom:12px;">
    ${conditions.length} condition${conditions.length !== 1 ? 's' : ''} • ${conditions.filter(c => c.statusOpen).length} open — ${scopeLabel}
  </div>
  ${sectionsHtml}
  ${conditions.length === 0 ? '<p style="color:#6b7280;font-size:13px;">No conditions found.</p>' : ''}
  <div style="margin-top:24px;padding-top:8px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;">
    Printed ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()} • ${company?.name || 'Lendwise Mortgage Inc.'}
  </div>
</body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  /**
   * Download condition template document
   */
  const handleDownloadConditionDoc = async (externalId: string, templateName: string, borrowerPairId?: string) => {
    if (!loanId) {
return;
}

    try {
      const token = await getJwt();

      if (!token) {
        toast({ title: 'Session expired', description: 'Please refresh the page.', variant: 'destructive' });
        return;
      }

      const requestBody: Record<string, string> = { action: 'get-condition-doc-internal', loanId, externalId };
      if (borrowerPairId) {
        requestBody.borrowerPairId = borrowerPairId;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to get document');
      }

      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');

      toast({
        title: 'Download started',
        description: `Downloading ${templateName}`
      });

    } catch (error) {
      log.error('Condition doc download error', { externalId, error });
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Unable to download document',
        variant: 'destructive'
      });
    }
  };

  /**
   * Open upload modal for a condition
   */
  const handleUploadClick = useCallback(async (condition: EncompassCondition) => {
    const token = await getJwt();
    if (!token) {
      toast({ title: 'Session expired', description: 'Please refresh the page.', variant: 'destructive' });
      return;
    }
    setSelectedCondition(condition);
    setUploadModalOpen(true);
  }, [getJwt, toast]);

  /**
   * Handle Fill & Sign for interactive warranty forms (admin/underwriter only)
   */
  const canFillWarranty = user?.roles?.some((r: string) => r === 'admin' || r === 'underwriter') ?? false;

  const handleFillAndSign = useCallback(async (condition: EncompassCondition) => {
    const token = await getJwt();
    if (!token) {
      toast({ title: 'Session expired', description: 'Please refresh the page.', variant: 'destructive' });
      return;
    }
    setWarrantyCondition(condition);
    setWarrantyModalOpen(true);
  }, [getJwt, toast]);

  const handleWarrantySuccess = useCallback(() => {
    toast({
      title: 'Warranty form submitted',
      description: 'PDF uploaded to eFolder and condition cleared.'
    });
    setWarrantyModalOpen(false);
    setWarrantyCondition(null);
    fetchPortalData();
  }, [toast, fetchPortalData]);

  /**
   * Handle successful upload
   */
  const handleUploadSuccess = useCallback((uploadId: string, confirmationNumber: string) => {
    toast({
      title: 'Document uploaded',
      description: `Confirmation: ${confirmationNumber}. Click "Submit to Processing" when ready.`
    });
    setUploadModalOpen(false);
    setSelectedCondition(null);
    fetchPortalData();
  }, [toast, fetchPortalData]);

  /**
   * Submit pending uploads to Encompass and notify processing team
   */
  const handleSubmitToProcessing = async () => {
    if (!loanId || submittingToProcessing) {
return;
}

    try {
      setSubmittingToProcessing(true);
      const token = await getJwt();

      if (!token) {
        toast({ title: 'Session expired', description: 'Please refresh the page.', variant: 'destructive' });
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'submit-to-processing-internal', loanId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit documents');
      }

      toast({
        title: 'Documents submitted!',
        description: result.message
      });

      fetchPortalData();

    } catch (error) {
      log.error('Submit to processing error', { error });
      toast({
        title: 'Submission failed',
        description: error instanceof Error ? error.message : 'Unable to submit documents',
        variant: 'destructive'
      });
    } finally {
      setSubmittingToProcessing(false);
    }
  };

  // Memoize encompassData to prevent modal reset on every render
  // Must be before early returns to satisfy React's Rules of Hooks
  const property = state.data?.loanData?.property;
  const encompassData = useMemo(() => ({
    projectClassification: property?.projectClassification || undefined,
    isNonWarrantable: property?.isNonWarrantable ?? false
  }), [property?.projectClassification, property?.isNonWarrantable]);

  // Error states
  if (state.error) {
    return (
      <PortalErrorState
        error={state.error}
        errorCode={state.errorCode}
        onRetry={state.errorCode === 'SERVER_ERROR' ? () => fetchPortalData() : undefined}
      />
    );
  }

  // Loading state
  if (state.loading || !state.data) {
    return <PortalLoadingState />;
  }

  const { loanData, loanOfficer, company, templates, uploads, conditionNotes, additionalCcRecipients } = state.data;

  const allConditions = [...loanData.conditions.ptd, ...loanData.conditions.ptf, ...loanData.conditions.ac];
  const openConditions = allConditions.filter(c => c.statusOpen);
  const clearedConditions = allConditions.filter(c => !c.statusOpen);
  const cleanApprovalEligible = isCleanApprovalEligible(state.data);

  const handleCleanApprovalOpenChange = (nextOpen: boolean) => {
    setCleanApprovalSheetOpen(nextOpen);
    if (!nextOpen) {
      removeCleanApprovalQuery();
    }
  };

  // Extract unique owners from all conditions for filter dropdown
  const uniqueOwners = Array.from(
    new Set(allConditions.map(c => c.owner?.entityName || 'Unassigned'))
  ).sort();

  // Helper to filter conditions by owner
  const filterByOwner = (conditions: EncompassCondition[]) => {
    if (ownerFilter === 'all') {
return conditions;
}
    return conditions.filter(c => (c.owner?.entityName || 'Unassigned') === ownerFilter);
  };

  return (
    <div className="h-screen overflow-y-auto bg-background flex flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 md:px-8">
          <div className="relative flex items-center justify-between">
            {/* Left - Loan Info */}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold">Loan Conditions</h1>
            </div>

            {/* Center - Company Name + Lock Status */}
            <div className="absolute left-1/2 -translate-x-1/2 hidden lg:flex items-center gap-4">
              {loanData.isFileLocked && (
                <div
                  className="flex items-center gap-2 text-red-600 cursor-default"
                  title={`${loanData.fileLockedByName || 'Another user'} has this loan open in Encompass. Reassignments are queued and apply automatically on unlock; uploads may be delayed.`}
                >
                  <Lock className="h-5 w-5" />
                  <span className="text-base font-semibold">{loanData.fileLockedByName || 'Locked'}</span>
                </div>
              )}
              <span className="text-2xl font-bold bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] bg-clip-text text-transparent tracking-tight">
                Lendwise Mortgage Inc.
              </span>
            </div>

            {/* Right - Action Buttons */}
            <div className="flex items-center gap-2">
              {/* View Portal Preview */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-300 text-slate-600 hover:bg-slate-50"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">View Portal</span>
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem
                    onClick={() => {
 setPreviewPortalType('title_escrow'); setPreviewSheetOpen(true); 
}}
                    className="cursor-pointer"
                  >
                    <Building className="h-4 w-4 mr-2" />
                    View Title/Escrow Portal
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
 setPreviewPortalType('borrower'); setPreviewSheetOpen(true); 
}}
                    className="cursor-pointer"
                  >
                    <User className="h-4 w-4 mr-2" />
                    View Borrower Portal
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Revoke Portal Access — only visible when active tokens exist */}
              {(() => {
                const activeList = [
                  activeTokens.loProcessor && { type: 'borrower' as const, token: activeTokens.loProcessor, label: 'Borrower Portal' },
                  activeTokens.titleEscrow && { type: 'titleEscrow' as const, token: activeTokens.titleEscrow, label: 'Title/Escrow Portal' }
                ].filter(Boolean) as Array<{ type: 'borrower' | 'titleEscrow'; token: ConditionsPortalToken; label: string }>;

                if (activeList.length === 0) {
return null;
}

                const handleClosePortals = async () => {
                  setClosingPortals(true);
                  try {
                    const toRevoke = activeList.length === 1
                      ? activeList
                      : activeList.filter(p => portalCloseSelections[p.type]);

                    let revokedCount = 0;
                    for (const portal of toRevoke) {
                      const success = await ConditionsPortalService.revokeToken({
                        tokenId: portal.token.id,
                        reason: 'Portal access revoked by staff',
                        revokedByUserId: user?.id
                      });
                      if (success) {
revokedCount++;
}
                    }

                    if (revokedCount > 0) {
                      toast({
                        title: revokedCount === 1 ? 'Portal Access Revoked' : `${revokedCount} Portals Revoked`,
                        description: 'Access has been revoked immediately.'
                      });
                      // Refresh tokens
                      if (loanId) {
                        const tokens = await ConditionsPortalService.getActiveTokensByType(loanId);
                        setActiveTokens(tokens);
                      }
                    }
                  } catch (err) {
                    log.error('Failed to revoke portal access', {}, err instanceof Error ? err : new Error(String(err)));
                    toast({ title: 'Failed to Revoke Portal Access', description: 'Please try again.', variant: 'destructive' });
                  } finally {
                    setClosingPortals(false);
                    setClosePortalDialogOpen(false);
                    setPortalCloseSelections({ borrower: false, titleEscrow: false });
                  }
                };

                const buttonLabel = 'Revoke Portal Access';

                // Single portal: simple button → confirm dialog
                if (activeList.length === 1) {
                  return (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => setClosePortalDialogOpen(true)}
                        disabled={closingPortals}
                      >
                        <ShieldX className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">{buttonLabel}</span>
                      </Button>
                      <AlertDialog open={closePortalDialogOpen} onOpenChange={setClosePortalDialogOpen}>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke {activeList[0].label} Access?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will immediately revoke access for {activeList[0].token.recipientName || 'the recipient'}.
                              They will no longer be able to view conditions or upload documents.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel disabled={closingPortals}>Cancel</AlertDialogCancel>
                            <Button
                              onClick={handleClosePortals}
                              disabled={closingPortals}
                              variant="destructive"
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {closingPortals ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                              Revoke Access
                            </Button>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  );
                }

                // Two portals: dropdown with checkboxes → confirm dialog
                const selectedCount = (portalCloseSelections.borrower ? 1 : 0) + (portalCloseSelections.titleEscrow ? 1 : 0);

                return (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          disabled={closingPortals}
                        >
                          <ShieldX className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">{buttonLabel}</span>
                          <ChevronDown className="h-4 w-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72 p-3">
                        <p className="text-xs font-medium text-slate-500 mb-2">Select portals to revoke:</p>
                        {activeList.map(p => (
                          <label
                            key={p.type}
                            className="flex items-center gap-2 px-2 py-2 rounded hover:bg-red-50 cursor-pointer"
                          >
                            <Checkbox
                              checked={portalCloseSelections[p.type]}
                              onCheckedChange={(checked) =>
                                setPortalCloseSelections(prev => ({ ...prev, [p.type]: !!checked }))
                              }
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700">{p.label}</p>
                              <p className="text-xs text-slate-500 truncate">{p.token.recipientName || p.token.recipientEmail}</p>
                            </div>
                          </label>
                        ))}
                        <Button
                          size="sm"
                          variant="destructive"
                          className="w-full mt-2"
                          disabled={selectedCount === 0}
                          onClick={() => setClosePortalDialogOpen(true)}
                        >
                          Revoke Access to {selectedCount === 0 ? 'Selected Portals' : `${selectedCount} Portal${selectedCount !== 1 ? 's' : ''}`}
                        </Button>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <AlertDialog open={closePortalDialogOpen} onOpenChange={setClosePortalDialogOpen}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke Access to {selectedCount} Portal{selectedCount !== 1 ? 's' : ''}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will immediately revoke access. Recipients will no longer be able to view conditions or upload documents.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={closingPortals}>Cancel</AlertDialogCancel>
                          <Button
                            onClick={handleClosePortals}
                            disabled={closingPortals}
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {closingPortals ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            Revoke Access
                          </Button>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                );
              })()}

              {/* Assign Conditions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#183C2E]/30 text-[#183C2E] hover:bg-[#183C2E]/5"
                  >
                    <span className="hidden sm:inline">Assign Conditions</span>
                    <span className="sm:hidden">Assign</span>
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem
                    onClick={() => setBulkReassignModalOpen(true)}
                    className="cursor-pointer hover:bg-[#183C2E]/10 focus:bg-[#183C2E]/10"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Reassign to Team Member
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTitleEscrowModalOpen(true)}
                    className="cursor-pointer hover:bg-[#183C2E]/10 focus:bg-[#183C2E]/10"
                  >
                    <Building className="h-4 w-4 mr-2" />
                    Route to Title & Escrow
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setBorrowerAssignModalOpen(true)}
                    className="cursor-pointer hover:bg-[#183C2E]/10 focus:bg-[#183C2E]/10"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Assign to Borrower
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setCleanApprovalSheetOpen(true)}
                    disabled={!cleanApprovalEligible}
                    className="cursor-pointer items-start hover:bg-[#183C2E]/10 focus:bg-[#183C2E]/10"
                  >
                    <FileCheck2 className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
                    <div>
                      <p>Create Clean Approval</p>
                      {!cleanApprovalEligible && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Available after conditional approval
                        </p>
                      )}
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* New Data Available Banner */}
      {hasNewData && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 py-3 md:px-8">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-blue-800">
                <RefreshCw className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Conditions have been updated.
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={applyPendingData}
              >
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Refresh for latest
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* File Lock Warning Banner */}
      {loanData.isFileLocked && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 py-3 md:px-8">
            <div className="flex items-center gap-2 text-amber-800">
              <Lock className="h-4 w-4 shrink-0" />
              <span className="text-sm font-medium">
                {loanData.fileLockedByName || 'Someone'} has this loan open in Encompass. Reassignments made here are queued and apply automatically on unlock; uploads may be delayed.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid lg:grid-cols-[180px_1fr_2fr] gap-6">
            {/* Timeline Column */}
            <div className="hidden lg:block sticky top-4 self-start">
              {loanData.milestoneHistory && loanData.milestoneHistory.length > 0 && (
                <MilestoneTimeline
                  milestones={loanData.milestoneHistory}
                  currentMilestone={loanData.currentMilestone}
                />
              )}
            </div>

            {/* Cards Column - Loan Summary */}
            <div className="space-y-6">
              {/* Loan Summary Card */}
              <Card className="border-[#183C2E]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">
                    Loan #{loanData.loanNumber}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Borrower</p>
                      <p className="font-medium">{loanData.borrower.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Property</p>
                      <p className="font-medium">
                        {loanData.property.address}
                        <br />
                        <span className="text-muted-foreground">
                          {loanData.property.city}, {loanData.property.state} {loanData.property.zip}
                        </span>
                      </p>
                    </div>
                  </div>
                  {/* Loan Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Loan Amount</p>
                      <p className="font-semibold">${loanData.loan.totalLoanAmount.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">LTV / CLTV</p>
                      <p className="font-semibold">{loanData.loan.ltv ? loanData.loan.ltv.toFixed(2) : '—'} / {loanData.loan.cltv ? `${loanData.loan.cltv.toFixed(2)}%` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">DTI</p>
                      <p className="font-semibold">{loanData.loan.frontRatio ? loanData.loan.frontRatio.toFixed(2) : '—'} / {loanData.loan.backRatio ? `${loanData.loan.backRatio.toFixed(2)}%` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Rate</p>
                      <p className="font-semibold">{loanData.lock.noteRate ? `${loanData.lock.noteRate.toFixed(3)}%` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Occupancy</p>
                      <p className="font-semibold">{loanData.loan.occupancy || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Transaction</p>
                      <p className="font-semibold">{loanData.loan.purpose || '—'}</p>
                    </div>
                  </div>

                  {state.data?.documentsNeededBy && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Clock className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-muted-foreground">Documents Needed By</span>
                      <span className="text-sm font-semibold text-red-600">{state.data.documentsNeededBy}</span>
                    </div>
                  )}

                  {/* Download PDF Button */}
                  <Button
                    className="w-full bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E] text-white"
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {downloadingPdf ? 'Generating PDF...' : 'Download Approval Letter'}
                  </Button>

                  {/* View Document Center Button */}
                  <Button
                    variant="outline"
                    className="w-full border-[#183C2E] text-[#183C2E] hover:bg-[#183C2E]/5"
                    onClick={() => window.open(`/internal/documents/${loanId}`, '_blank', 'noopener')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Document Center
                  </Button>

                  {/* Print Conditions Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full text-xs">
                        <Printer className="h-3.5 w-3.5 mr-1.5" />
                        Print Conditions
                        <ChevronDown className="h-3.5 w-3.5 ml-auto" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      <DropdownMenuItem onClick={() => handlePrintConditions({ owner: 'all' })}>
                        All Owners
                      </DropdownMenuItem>
                      {uniqueOwners.map(owner => (
                        <DropdownMenuItem key={owner} onClick={() => handlePrintConditions({ owner })}>
                          {owner}
                        </DropdownMenuItem>
                      ))}
                      {titleEscrowAssignedIds.size > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handlePrintConditions('title_escrow')}>
                            Title/Escrow View
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Loss Payee / Mortgagee Clause */}
                  <div className="pt-4 mt-4 border-t">
                    <div className="flex items-start gap-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                      <Building className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-blue-800 block text-xs font-semibold uppercase tracking-wide">
                          Loss Payee / Mortgagee Clause
                        </span>
                        <div className="text-sm text-gray-900 mt-1 space-y-0.5">
                          <p className="font-medium">Lendwise Mortgage, Inc. ISAOA</p>
                          <p>21800 Oxnard Street #220</p>
                          <p>Woodland Hills, CA 91367</p>
                          <p className="text-gray-600">Loan #: {loanData.loanNumber}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Investor / Lock Info Card */}
              {loanData.lock.investor && (
                <Card className="border-[#183C2E]">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">
                      Investor
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="font-semibold">{loanData.lock.investor}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {loanData.loan.docType && (
                        <div className="col-span-2">
                          <p className="text-muted-foreground">Doc Type</p>
                          <p className="font-semibold">{loanData.loan.docType}</p>
                        </div>
                      )}
                      {loanData.lock.lockDate && (
                        <div>
                          <p className="text-muted-foreground">Locked</p>
                          <p className="font-semibold">{parseAsLocalDate(loanData.lock.lockDate).toLocaleDateString()}</p>
                        </div>
                      )}
                      {loanData.lock.lockExpires && (
                        <div>
                          <p className="text-muted-foreground">Lock Expires</p>
                          <p className="font-semibold">
                            {parseAsLocalDate(loanData.lock.lockExpires).toLocaleDateString()}
                            {(() => {
                              const daysLeft = Math.ceil((parseAsLocalDate(loanData.lock.lockExpires).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                              if (daysLeft > 0) {
return <span className="text-muted-foreground ml-1">({daysLeft}d)</span>;
}
                              if (daysLeft === 0) {
return <span className="text-amber-600 ml-1">(Today)</span>;
}
                              return <span className="text-red-600 ml-1">(Expired)</span>;
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Conditions Progress */}
              <Card className="border-[#183C2E]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">
                    Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#10B981] transition-all duration-500"
                          style={{
                            width: `${allConditions.length > 0 ? (clearedConditions.length / allConditions.length) * 100 : 0}%`
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">
                      {clearedConditions.length}/{allConditions.length}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {openConditions.length === 0
                      ? 'All conditions cleared!'
                      : `${openConditions.length} condition${openConditions.length === 1 ? '' : 's'} remaining`}
                  </p>
                </CardContent>
              </Card>

              {/* Pending Documents - Ready to Submit */}
              {(() => {
                const pendingUploads = uploads.filter(u => u.encompassSyncStatus === 'pending');
                const syncedUploads = uploads.filter(u => u.encompassSyncStatus === 'synced');

                if (pendingUploads.length === 0 && syncedUploads.length === 0) {
return null;
}

                return (
                  <Card className={pendingUploads.length > 0 ? 'border-amber-300 bg-amber-50/50' : 'border-[#183C2E]'}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Your Documents
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Pending uploads */}
                      {pendingUploads.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                            Ready to Submit ({pendingUploads.length})
                          </p>
                          {pendingUploads.map((upload) => (
                            <div
                              key={upload.id}
                              className="flex items-center gap-2 p-2 bg-white rounded border border-amber-200"
                            >
                              <FileText className="h-4 w-4 text-amber-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{upload.originalFilename}</p>
                                <p className="text-xs text-muted-foreground">{upload.conditionName}</p>
                              </div>
                              <Clock className="h-3 w-3 text-amber-500" />
                            </div>
                          ))}

                          {/* Submit Button */}
                          <Button
                            className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                            onClick={handleSubmitToProcessing}
                            disabled={submittingToProcessing}
                          >
                            {submittingToProcessing ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                Submit to Processing
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-center text-muted-foreground">
                            Documents will be sent to Encompass and the processing team
                          </p>
                        </div>
                      )}

                      {/* Already synced uploads */}
                      {syncedUploads.length > 0 && (
                        <div className="space-y-2">
                          {pendingUploads.length > 0 && <div className="border-t pt-3" />}
                          <p className="text-xs font-medium text-green-700 uppercase tracking-wide">
                            Submitted ({syncedUploads.length})
                          </p>
                          {syncedUploads.map((upload) => (
                            <div
                              key={upload.id}
                              className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200"
                            >
                              <FileText className="h-4 w-4 text-green-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{upload.originalFilename}</p>
                                <p className="text-xs text-muted-foreground">{upload.conditionName}</p>
                              </div>
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Loan Team Contacts */}
              {(loanData.processor || loanData.underwriter) && (
                <Card className="border-[#183C2E]">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">
                      Loan Team
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loanData.processor && (
                      <TeamContactRow label="Processor" contact={loanData.processor} />
                    )}
                    {loanData.underwriter && (
                      <TeamContactRow label="Underwriter" contact={loanData.underwriter} />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Title & Escrow Contacts */}
              {loanData.titleEscrow && (loanData.titleEscrow.companyName || loanData.titleEscrow.contactName) && (
                <Card className="border-[#183C2E]">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">
                      Title & Escrow
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loanData.titleEscrow.companyName && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{loanData.titleEscrow.companyName}</span>
                        </div>
                        {(loanData.titleEscrow.address || loanData.titleEscrow.city) && (
                          <p className="text-xs text-muted-foreground pl-6">
                            {loanData.titleEscrow.address && <span>{loanData.titleEscrow.address}<br /></span>}
                            {loanData.titleEscrow.city && `${loanData.titleEscrow.city}, `}
                            {loanData.titleEscrow.state} {loanData.titleEscrow.zip}
                          </p>
                        )}
                      </div>
                    )}
                    {loanData.titleEscrow.contactName && (
                      <TitleEscrowContactRow label="Escrow Officer" titleEscrow={loanData.titleEscrow} />
                    )}
                  </CardContent>
                </Card>
              )}

            </div>

            {/* Right Column - Conditions */}
            <div className="space-y-6">

              <Card className="border-[#183C2E]">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      Conditions
                    </CardTitle>
                    <div className="flex items-center gap-4">
                      {/* Timing Filter Dropdown */}
                      <Select value={timingFilter} onValueChange={(v) => setTimingFilter(v as typeof timingFilter)}>
                        <SelectTrigger className="h-8 w-[140px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Conditions</SelectItem>
                          <SelectItem value="ptd">Prior to Docs</SelectItem>
                          <SelectItem value="ptf">Prior to Funding</SelectItem>
                          <SelectItem value="ac">At Closing</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* Owner Filter Dropdown */}
                      <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                        <SelectTrigger className="h-8 w-[160px] text-xs">
                          <SelectValue placeholder="All Owners" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Owners</SelectItem>
                          {uniqueOwners.map(owner => (
                            <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Show Only Open Toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          id="show-open-only"
                          checked={showOnlyOpen}
                          onCheckedChange={setShowOnlyOpen}
                        />
                        <Label htmlFor="show-open-only" className="text-xs text-muted-foreground cursor-pointer">
                          Open only
                        </Label>
                      </div>
                      {state.lastRefresh && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Updated {state.lastRefresh.toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* PTD - Prior to Docs */}
                  {(timingFilter === 'all' || timingFilter === 'ptd') && (() => {
                    const filteredPtd = filterByOwner(
                      showOnlyOpen
                        ? loanData.conditions.ptd.filter(c => c.statusOpen)
                        : loanData.conditions.ptd
                    );
                    if (filteredPtd.length > 0) {
                      return (
                        <ConditionsSection
                          title="Prior to Docs"
                          conditions={filteredPtd}
                          templates={templates}
                          uploads={uploads}
                          notes={conditionNotes}
                          onDownloadTemplate={handleDownloadConditionDoc}
                          onUploadClick={handleUploadClick}
                          onFillAndSign={canFillWarranty ? handleFillAndSign : undefined}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      );
                    } else if (ownerFilter !== 'all') {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Circle className="h-5 w-5 text-gray-300" />
                            <h3 className="font-semibold text-gray-400">Prior to Docs</h3>
                          </div>
                          <p className="text-sm text-muted-foreground pl-7">
                            No conditions assigned to {ownerFilter}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* PTF - Prior to Funding */}
                  {(timingFilter === 'all' || timingFilter === 'ptf') && (() => {
                    const filteredPtf = filterByOwner(
                      showOnlyOpen
                        ? loanData.conditions.ptf.filter(c => c.statusOpen)
                        : loanData.conditions.ptf
                    );
                    if (filteredPtf.length > 0) {
                      return (
                        <ConditionsSection
                          title="Prior to Funding"
                          conditions={filteredPtf}
                          templates={templates}
                          uploads={uploads}
                          notes={conditionNotes}
                          onDownloadTemplate={handleDownloadConditionDoc}
                          onUploadClick={handleUploadClick}
                          onFillAndSign={canFillWarranty ? handleFillAndSign : undefined}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      );
                    } else if (ownerFilter !== 'all') {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Circle className="h-5 w-5 text-gray-300" />
                            <h3 className="font-semibold text-gray-400">Prior to Funding</h3>
                          </div>
                          <p className="text-sm text-muted-foreground pl-7">
                            No conditions assigned to {ownerFilter}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* AC - At Closing */}
                  {(timingFilter === 'all' || timingFilter === 'ac') && (() => {
                    const filteredAc = filterByOwner(
                      showOnlyOpen
                        ? loanData.conditions.ac.filter(c => c.statusOpen)
                        : loanData.conditions.ac
                    );
                    if (filteredAc.length > 0) {
                      return (
                        <ConditionsSection
                          title="At Closing"
                          conditions={filteredAc}
                          templates={templates}
                          uploads={uploads}
                          notes={conditionNotes}
                          onDownloadTemplate={handleDownloadConditionDoc}
                          onUploadClick={handleUploadClick}
                          onFillAndSign={canFillWarranty ? handleFillAndSign : undefined}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      );
                    } else if (ownerFilter !== 'all') {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Circle className="h-5 w-5 text-gray-300" />
                            <h3 className="font-semibold text-gray-400">At Closing</h3>
                          </div>
                          <p className="text-sm text-muted-foreground pl-7">
                            No conditions assigned to {ownerFilter}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Empty state */}
                  {allConditions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No conditions found for this loan.
                    </div>
                  )}

                  {/* All cleared state */}
                  {allConditions.length > 0 && openConditions.length === 0 && showOnlyOpen && (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-green-700 font-medium">All conditions have been cleared!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Toggle "Open only" off to view cleared conditions.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {selectedCondition && loanId && (
        <ConditionUploadModal
          open={uploadModalOpen}
          onOpenChange={(open) => {
            setUploadModalOpen(open);
            if (!open) {
setSelectedCondition(null);
}
          }}
          condition={selectedCondition}
          loanId={loanId}
          isInternal={true}
          jwt={jwt || undefined}
          onSuccess={handleUploadSuccess}
          isFileLocked={loanData.isFileLocked}
          fileLockedByName={loanData.fileLockedByName}
        />
      )}

      {/* Title/Escrow Assignment Modal */}
      <TitleEscrowAssignModal
        open={titleEscrowModalOpen}
        onOpenChange={setTitleEscrowModalOpen}
        conditions={allConditions.filter(c => c.statusOpen)}
        loanId={loanId}
        isInternal={true}
        jwt={jwt || undefined}
        titleEscrowContact={loanData.titleEscrow}
        loanData={loanData}
        loanOfficer={loanOfficer}
        company={company}
        templates={templates}
        additionalCcRecipients={additionalCcRecipients}
        onSuccess={(portalUrl) => {
          toast({
            title: 'Title/Escrow Portal Created',
            description: 'A notification has been sent to the loan team.'
          });
          navigator.clipboard.writeText(portalUrl).then(() => {
            toast({
              title: 'Link Copied',
              description: 'The portal link has been copied to your clipboard.'
            });
          });
          // Refresh portal data to pick up new deadline and token state
          fetchPortalData();
          fetchAllAssignments();
        }}
      />

      {/* Borrower Assignment Modal */}
      <BorrowerAssignModal
        open={borrowerAssignModalOpen}
        onOpenChange={setBorrowerAssignModalOpen}
        conditions={allConditions}
        loanId={loanId}
        existingAssignments={borrowerAssignedIds}
        existingNotes={borrowerNotes}
        onSuccess={() => {
          toast({
            title: 'Borrower Conditions Updated',
            description: 'The borrower condition assignments have been saved.'
          });
          fetchAllAssignments();
        }}
      />

      {/* Bulk Reassign Modal */}
      <BulkReassignModal
        open={bulkReassignModalOpen}
        onOpenChange={setBulkReassignModalOpen}
        conditions={allConditions.filter(c => c.statusOpen)}
        loanId={loanId}
        isInternal={true}
        jwt={jwt || undefined}
        onSuccess={() => {
          toast({
            title: 'Conditions Reassigned',
            description: 'The conditions have been updated in Encompass.'
          });
          fetchPortalData();
        }}
        isFileLocked={loanData.isFileLocked}
        fileLockedByName={loanData.fileLockedByName}
      />

      {/* Condo/PUD Warranty Form Modal (admin/underwriter only) */}
      {warrantyCondition && loanId && jwt && (
        <CondoPudWarrantyModal
          open={warrantyModalOpen}
          onOpenChange={(open) => {
            setWarrantyModalOpen(open);
            if (!open) {
setWarrantyCondition(null);
}
          }}
          condition={warrantyCondition}
          loanId={loanId}
          loanData={{
            loanNumber: loanData.loanNumber || '',
            borrowerName: loanData.borrower?.name || '',
            propertyAddress: [
              loanData.property?.address,
              loanData.property?.city,
              `${loanData.property?.state || ''} ${loanData.property?.zip || ''}`.trim()
            ].filter(Boolean).join(', ')
          }}
          encompassData={encompassData}
          jwt={jwt}
          user={{
            fullName: user?.fullName || user?.email || '',
            title: undefined
          }}
          onSuccess={handleWarrantySuccess}
        />
      )}

      {/* Portal Preview Sheet */}
      <PortalPreviewSheet
        open={previewSheetOpen}
        onOpenChange={setPreviewSheetOpen}
        portalType={previewPortalType}
        loanData={loanData}
        loanOfficer={loanOfficer}
        company={company}
        templates={templates}
        uploads={uploads}
        conditionNotes={conditionNotes}
        titleEscrowAssignedIds={titleEscrowAssignedIds}
        titleEscrowNotes={titleEscrowNotes}
        borrowerAssignedIds={borrowerAssignedIds}
        borrowerNotes={borrowerNotes}
        documentsNeededBy={state.data?.documentsNeededBy}
      />

      {loanId && (
        <CleanApprovalSheet
          loanId={loanId}
          open={cleanApprovalSheetOpen}
          onOpenChange={handleCleanApprovalOpenChange}
        />
      )}
    </div>
  );
};

/**
 * Loading skeleton
 */
const PortalLoadingState = () => (
  <div className="h-screen overflow-y-auto bg-background flex flex-col">
    <div className="p-4 md:p-6 border-b bg-card">
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-24 mt-1" />
        </div>
      </div>
    </div>
    <div className="flex-1 p-4 md:p-8">
      <div className="max-w-[1480px] mx-auto grid lg:grid-cols-[180px_1fr_2fr] gap-6">
        <div className="hidden lg:block">
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Error state
 */
const PortalErrorState = ({
  error,
  errorCode,
  onRetry
}: {
  error: string;
  errorCode: string | null;
  onRetry?: () => void;
}) => {
  const getErrorIcon = () => {
    switch (errorCode) {
      case 'UNAUTHORIZED':
        return <AlertCircle className="h-16 w-16 text-destructive" />;
      case 'NOT_FOUND':
        return <AlertCircle className="h-16 w-16 text-warning" />;
      default:
        return <AlertCircle className="h-16 w-16 text-muted-foreground" />;
    }
  };

  const getErrorTitle = () => {
    switch (errorCode) {
      case 'UNAUTHORIZED':
        return 'Session Expired';
      case 'NOT_FOUND':
        return 'Loan Not Found';
      default:
        return 'Something Went Wrong';
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="mb-6">{getErrorIcon()}</div>
          <h1 className="text-2xl font-bold mb-2">{getErrorTitle()}</h1>
          <p className="text-muted-foreground mb-6">{error}</p>

          <div className="flex justify-center gap-2">
            {errorCode === 'UNAUTHORIZED' ? (
              <Button onClick={() => window.location.href = '/'}>
                Log In
              </Button>
            ) : onRetry ? (
              <Button onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Team contact row component
 */
const TeamContactRow = ({
  label,
  contact
}: {
  label: string;
  contact: LoanTeamContact;
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="pl-6 space-y-1">
        <p className="font-medium text-sm">{contact.name}</p>
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Mail className="h-3 w-3" />
            <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone.replace(/\D/g, '')}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Phone className="h-3 w-3" />
            <span>{contact.phone}</span>
          </a>
        )}
      </div>
    </div>
  );
};

/**
 * Title/Escrow contact row component
 */
const TitleEscrowContactRow = ({
  label,
  titleEscrow
}: {
  label: string;
  titleEscrow: TitleEscrowInfo;
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="pl-6 space-y-1">
        <p className="font-medium text-sm">{titleEscrow.contactName}</p>
        {titleEscrow.email && (
          <a
            href={`mailto:${titleEscrow.email}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Mail className="h-3 w-3" />
            <span className="truncate">{titleEscrow.email}</span>
          </a>
        )}
        {titleEscrow.phone && (
          <a
            href={`tel:${titleEscrow.phone.replace(/\D/g, '')}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Phone className="h-3 w-3" />
            <span>{titleEscrow.phone}</span>
          </a>
        )}
      </div>
    </div>
  );
};

export default InternalConditionsPortal;
